// -- Run in Supabase SQL editor if deletes are blocked by RLS:
// CREATE POLICY "Admins can delete products"
//   ON products FOR DELETE
//   USING (
//     EXISTS (
//       SELECT 1 FROM profiles
//       WHERE id = auth.uid()
//       AND role = 'admin'
//     )
//   );

import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { isArReady } from "@/lib/productUtils";
import { arDisplayStatus } from "@/lib/assetStatus";
import { needsRetrigger } from "@/lib/pipelineStages";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const SB_URL  = import.meta.env.VITE_SUPABASE_URL  as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const PAGE_SIZE = 24;

const CATEGORY_GROUPS = [
  {
    label: "Hearth",
    options: [
      { value: "gas_fireplace",       label: "Gas Fireplace / Insert" },
      { value: "wood_fireplace",      label: "Wood Fireplace / Stove" },
      { value: "pellet_stove",        label: "Pellet Stove / Insert" },
      { value: "electric_fireplace",  label: "Electric Fireplace" },
      { value: "fireplace_insert",    label: "Fireplace Insert" },
      { value: "gas_logs",            label: "Gas Logs & Burners" },
      { value: "chimney_venting",     label: "Chimney & Venting" },
      { value: "fireplace_accessory", label: "Hearth Accessory" },
    ],
  },
  {
    label: "Patio & Outdoor",
    options: [
      { value: "patio_furniture",   label: "Patio Furniture" },
      { value: "outdoor_kitchen",   label: "Outdoor Kitchen" },
      { value: "pergola_gazebo",    label: "Pergola / Gazebo" },
      { value: "fire_pit",          label: "Fire Pit / Bowl" },
      { value: "patio_heater",      label: "Patio Heater" },
      { value: "outdoor_accessory", label: "Outdoor Accessory" },
    ],
  },
  {
    label: "Barbecue",
    options: [
      { value: "gas_grill",      label: "Gas Grill" },
      { value: "charcoal_grill", label: "Charcoal Grill" },
      { value: "pellet_grill",   label: "Pellet Grill / Smoker" },
      { value: "kamado_grill",   label: "Kamado Grill" },
      { value: "built_in_grill", label: "Built-in Grill" },
      { value: "bbq_accessory",  label: "BBQ Accessory" },
    ],
  },
] as const;

const CATEGORY_MAP: Record<string, string> = Object.fromEntries(
  CATEGORY_GROUPS.flatMap((g) => g.options.map((o) => [o.value, o.label]))
);

// Category group → colour token (used by CategoryBadge)
const CATEGORY_GROUP_MAP: Record<string, "hearth" | "patio" | "bbq"> = {};
CATEGORY_GROUPS.forEach((g) => {
  const key =
    g.label === "Hearth"          ? "hearth" :
    g.label === "Patio & Outdoor" ? "patio"  : "bbq";
  g.options.forEach((o) => { CATEGORY_GROUP_MAP[o.value] = key; });
});

const GROUP_COLORS: Record<string, string> = {
  hearth: "bg-orange-900/30 text-orange-300 border-orange-800/50",
  patio:  "bg-teal-900/30  text-teal-300   border-teal-800/50",
  bbq:    "bg-blue-900/30  text-blue-300   border-blue-800/50",
};

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface ProductRow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  manufacturer_id: string | null;
  manufacturer_name?: string;
  asset_status: string | null;
  pbr_status: string | null;
  geometry_status: string | null;
  glb_assembly_status: string | null;
  glb_pbr_url: string | null;
  usdz_pbr_url: string | null;
  consumer_specs_json: Record<string, unknown> | null;
  active: boolean;
  image_url: string | null;
  base_price: number | null;
  // How the product behaves once placed, and where its geometry came from.
  // 'wall' and null respectively for every photogrammetry product (M2 defaults).
  placement_type: string | null;
  geometry_source: string | null;
  material_source: string | null;
  created_at: string;
}

type SortKey      = "name" | "base_price" | "created_at";
type StatusFilter = "all" | "complete" | "processing" | "pending" | "failed" | "none" | "needs_attention";

/** Source of the mesh. `null` means photogrammetry — the historical default,
 *  and still the majority of the catalogue, so it gets a real label rather
 *  than a dash. Values are constrained by products_geometry_source_check. */
const GEOMETRY_SOURCE_LABELS: Record<string, string> = {
  photogrammetry:         "Photogrammetry",
  parametric_dimensions:  "Parametric",
  dxf_extrusion:          "DXF extrusion",
  rfa_cad:                "RFA CAD",
};

const PLACEMENT_TYPE_LABELS: Record<string, string> = {
  wall:          "Wall",
  floor:         "Floor",
  ceiling:       "Ceiling",
  ground_staked: "Ground",
};

/** Filter key for products whose geometry_source is null. Distinct from "" so
 *  "no filter" and "explicitly photogrammetry" stay separable. */
const PHOTOGRAMMETRY = "photogrammetry";

export function geometrySourceKey(value: string | null): string {
  return value ?? PHOTOGRAMMETRY;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

// needsRetrigger lives in src/lib/pipelineStages.ts. This page had its own copy
// with the same global-4-stage assumption AdminPipeline had: it would offer to
// re-run PBR on a swatch-library product, invoking Stability on a pergola —
// which Non-negotiable 7 forbids.

function relativeDate(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30)  return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/* ------------------------------------------------------------------ */
/* Badge / UI components                                               */
/* ------------------------------------------------------------------ */

const AR_META: Record<string, { label: string; classes: string }> = {
  ready:      { label: "AR Ready",    classes: "bg-green-900/40 text-green-400 border-green-800" },
  processing: { label: "Processing",  classes: "bg-orange-900/40 text-orange-400 border-orange-800" },
  pending:    { label: "Pending",     classes: "bg-orange-900/40 text-orange-400 border-orange-800" },
  failed:     { label: "Failed",      classes: "bg-red-900/40 text-red-400 border-red-800" },
};

function ArBadge({ status, usdzUrl }: { status: string | null; usdzUrl?: string | null }) {
  const meta = AR_META[status ?? ""] ?? { label: "No AR", classes: "bg-gray-800 text-gray-500 border-gray-700" };
  const cls  = `inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.classes}`;
  if (usdzUrl && status === "ready") {
    return (
      <a
        href={usdzUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`${cls} hover:opacity-80 transition cursor-pointer`}
        title="Preview in AR Quick Look"
      >
        {meta.label} ↗
      </a>
    );
  }
  return <span className={cls}>{meta.label}</span>;
}

/** Single badge that combines pipeline health + AR readiness. */
function PipelineBadge({ product }: { product: ProductRow }) {
  if (needsRetrigger(product)) {
    return (
      <span className="inline-flex items-center rounded-full border border-amber-700 bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-400">
        Needs Attention
      </span>
    );
  }
  const arStatus = arDisplayStatus(product);
  return (
    <ArBadge
      status={arStatus}
      usdzUrl={arStatus === "ready" ? product.usdz_pbr_url : null}
    />
  );
}

/**
 * Placement behaviour and geometry provenance, the two axes that decide how a
 * product is built and how it behaves once placed. Surfaced in the list because
 * without them a pergola and a wall fireplace are indistinguishable at a glance,
 * and they explain why a row shows no source files or a skipped PBR stage.
 *
 * Only the non-default values get a visible chip: with ~everything currently
 * 'wall' + photogrammetry, badging all of them would be noise that hides the
 * handful of rows that actually differ.
 */
function TypeBadges({
  placementType, geometrySource,
}: { placementType: string | null; geometrySource: string | null }) {
  const placement = placementType ?? "wall";
  const source = geometrySourceKey(geometrySource);
  const chips: { key: string; label: string; classes: string }[] = [];

  if (placement !== "wall") {
    chips.push({
      key: "placement",
      label: PLACEMENT_TYPE_LABELS[placement] ?? placement,
      classes: "bg-teal-900/30 text-teal-300 border-teal-800/50",
    });
  }
  if (source !== PHOTOGRAMMETRY) {
    chips.push({
      key: "source",
      label: GEOMETRY_SOURCE_LABELS[source] ?? source,
      classes: "bg-purple-900/30 text-purple-300 border-purple-800/50",
    });
  }

  if (chips.length === 0) {
    return <span className="text-xs text-gray-600" title="Wall-mounted, built from photogrammetry">—</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {chips.map((c) => (
        <span
          key={c.key}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${c.classes}`}
        >
          {c.label}
        </span>
      ))}
    </span>
  );
}

function CategoryBadge({ category }: { category: string | null }) {
  if (!category) return <span className="text-xs text-gray-600">—</span>;
  const group  = CATEGORY_GROUP_MAP[category];
  const colors = group ? GROUP_COLORS[group] : "bg-gray-800 text-gray-400 border-gray-700";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${colors}`}>
      {CATEGORY_MAP[category] ?? category}
    </span>
  );
}

function SpecBadge({ hasSpecs }: { hasSpecs: boolean }) {
  return hasSpecs
    ? <span className="inline-flex items-center rounded-full border border-orange-800 bg-orange-900/30 px-2 py-0.5 text-xs font-medium text-orange-400">✓ Specs</span>
    : <span className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900 px-2 py-0.5 text-xs font-medium text-gray-600">No specs</span>;
}

function ImageBadge({ hasImage }: { hasImage: boolean }) {
  return hasImage
    ? <span className="inline-flex items-center rounded-full border border-green-800 bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-400">✓ Image</span>
    : <span className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900 px-2 py-0.5 text-xs font-medium text-gray-600">No image</span>;
}

function ProductThumb({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return <img src={url} alt={name} className="h-10 w-10 rounded-lg object-cover border border-gray-800" />;
  }
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-800 bg-gray-800 text-lg">
      📦
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stat chip                                                            */
/* ------------------------------------------------------------------ */

function StatChip({ label, value, accent, dim }: { label: string; value: number; accent?: string; dim?: boolean }) {
  return (
    <div className={`rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-2 text-center min-w-[80px] ${dim ? "opacity-60" : ""}`}>
      <p className={`text-xl font-semibold tabular-nums ${accent ?? "text-white"}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminProducts() {
  const { session } = useAuth();
  const navigate    = useNavigate();
  const { toast }   = useToast();
  const jwt = session?.access_token ?? "";

  const [products,     setProducts]     = useState<ProductRow[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState<Set<string>>(new Set());

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [bulkConfirm,  setBulkConfirm]  = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rerunning,    setRerunning]    = useState(false);

  // Filters
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter,  setCategoryFilter]  = useState("");
  const [statusFilter,    setStatusFilter]    = useState<StatusFilter>("all");
  const [placementFilter, setPlacementFilter] = useState("");
  const [geomSrcFilter,   setGeomSrcFilter]   = useState("");
  const [sortKey,         setSortKey]         = useState<SortKey>("created_at");
  const [sortAsc,         setSortAsc]         = useState(false);
  const [page,            setPage]            = useState(0);

  // Debounce search by 300 ms
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page whenever any filter changes
  useEffect(() => { setPage(0); }, [categoryFilter, statusFilter, placementFilter, geomSrcFilter, sortKey, sortAsc]);

  /* ── Fetch ─────────────────────────────────────────────────────── */

  const fetchProducts = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/products` +
          `?select=id,name,description,category,manufacturer_id,asset_status,` +
          `pbr_status,geometry_status,glb_assembly_status,glb_pbr_url,usdz_pbr_url,` +
          `consumer_specs_json,active,image_url,base_price,` +
          `placement_type,geometry_source,material_source,` +
          `created_at,manufacturers(name)` +
          `&order=created_at.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setProducts(
          data.map((p: ProductRow & { manufacturers?: { name: string } }) => ({
            ...p,
            manufacturer_name: p.manufacturers?.name ?? "—",
          }))
        );
      }
    } catch (err) {
      console.error("[AdminProducts] fetchProducts error:", err);
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  /* ── Derived data ────────────────────────────────────────────────── */

  const filtered = useMemo(() => {
    let rows = [...products];

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      rows = rows.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        (p.manufacturer_name ?? "").toLowerCase().includes(q) ||
        (CATEGORY_MAP[p.category ?? ""] ?? "").toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q)
      );
    }

    if (placementFilter) {
      rows = rows.filter((p) => (p.placement_type ?? "wall") === placementFilter);
    }

    if (geomSrcFilter) {
      rows = rows.filter((p) => geometrySourceKey(p.geometry_source) === geomSrcFilter);
    }

    if (categoryFilter) {
      rows = rows.filter((p) => p.category === categoryFilter);
    }

    if (statusFilter !== "all") {
      if (statusFilter === "needs_attention") {
        rows = rows.filter((p) => needsRetrigger(p));
      } else if (statusFilter === "complete") {
        rows = rows.filter((p) => arDisplayStatus(p) === "ready");
      } else if (statusFilter === "none") {
        rows = rows.filter((p) => arDisplayStatus(p) === null);
      } else {
        // "processing" | "pending" | "failed" — compare against the normalised
        // status so `generating_texture` lands under Processing rather than
        // vanishing from every filter.
        rows = rows.filter((p) => arDisplayStatus(p) === statusFilter);
      }
    }

    rows.sort((a, b) => {
      let cmp = 0;
      if      (sortKey === "name")       cmp = a.name.localeCompare(b.name);
      else if (sortKey === "base_price") cmp = (a.base_price ?? 0) - (b.base_price ?? 0);
      else                               cmp = a.created_at.localeCompare(b.created_at);
      return sortAsc ? cmp : -cmp;
    });

    return rows;
  }, [products, debouncedSearch, categoryFilter, statusFilter, placementFilter, geomSrcFilter, sortKey, sortAsc]);

  // Stats reflect the current filtered set
  const stats = useMemo(() => ({
    total:          filtered.length,
    arReady:        filtered.filter((p) => isArReady(p)).length,
    needsAttention: filtered.filter((p) => needsRetrigger(p)).length,
    processing:     filtered.filter((p) => p.asset_status === "processing" || p.asset_status === "pending").length,
  }), [filtered]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.length > PAGE_SIZE
    ? filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : filtered;

  /* ── Actions ─────────────────────────────────────────────────────── */

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selected.size === paginated.length && paginated.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map((p) => p.id)));
    }
  }

  function exportCsv() {
    // Export selected products if any are selected, otherwise export all filtered
    const rows = selected.size > 0
      ? filtered.filter((p) => selected.has(p.id))
      : filtered;

    const cols = ["name", "description", "category", "manufacturer", "base_price",
                  "asset_status", "pbr_status", "geometry_status", "active", "created_at"];
    const data = rows.map((p) => [
      p.name,
      p.description ?? "",
      CATEGORY_MAP[p.category ?? ""] ?? p.category ?? "",
      p.manufacturer_name ?? "",
      p.base_price ?? "",
      p.asset_status ?? "",
      p.pbr_status ?? "",
      p.geometry_status ?? "",
      p.active ? "active" : "draft",
      p.created_at,
    ]);
    const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv    = [cols, ...data].map((r) => r.map(escape).join(",")).join("\n");
    const blob   = new Blob([csv], { type: "text/csv" });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement("a");
    a.href       = url;
    a.download   = `products-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function deleteProducts(ids: string[]) {
    if (ids.length === 0) return;
    setDeleteLoading(true);
    const inFilter = `(${ids.join(",")})`;
    const headers  = { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, Prefer: "return=minimal" };
    try {
      const res = await fetch(`${SB_URL}/rest/v1/products?id=in.${inFilter}`, { method: "DELETE", headers });
      if (!res.ok) {
        const text = await res.text();
        console.error("Delete error:", text.slice(0, 400));
        throw new Error(`Delete failed (${res.status}): ${text.slice(0, 200)}`);
      }
      setSelected(new Set());
      toast({ title: ids.length === 1 ? "Product deleted successfully" : `${ids.length} products deleted successfully` });
      await fetchProducts();
    } catch (err) {
      toast({ title: "Delete failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
      setBulkConfirm(false);
    }
  }

  async function bulkRerunPipeline() {
    if (!jwt || rerunning || selected.size === 0) return;
    setRerunning(true);
    const ids = Array.from(selected);
    try {
      await Promise.all(
        ids.map((id) =>
          fetch(`${SB_URL}/functions/v1/orchestrate-pipeline`, {
            method:  "POST",
            headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${jwt}` },
            body:    JSON.stringify({ product_id: id }),
          }).catch(() => {/* individual failures don't block the batch */})
        )
      );
      toast({ title: `Pipeline triggered for ${ids.length} product${ids.length !== 1 ? "s" : ""}` });
      setSelected(new Set());
      await fetchProducts();
    } catch (err) {
      toast({ title: "Re-run failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setRerunning(false);
    }
  }

  async function rerunSingle(id: string) {
    if (!jwt) return;
    try {
      await fetch(`${SB_URL}/functions/v1/orchestrate-pipeline`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${jwt}` },
        body:    JSON.stringify({ product_id: id }),
      });
      toast({ title: "Pipeline triggered" });
      // Optimistic update: mark null/failed stages as pending
      setProducts((prev) =>
        prev.map((row) => {
          if (row.id !== id) return row;
          return {
            ...row,
            pbr_status:          row.pbr_status      === null || row.pbr_status      === "failed" ? "pending" : row.pbr_status,
            geometry_status:     row.geometry_status  === null || row.geometry_status  === "failed" ? "pending" : row.geometry_status,
            glb_assembly_status: row.glb_assembly_status === null || row.glb_assembly_status === "failed" ? "pending" : row.glb_assembly_status,
          };
        })
      );
    } catch (err) {
      toast({ title: "Failed to trigger pipeline", description: err instanceof Error ? err.message : "Unknown error" });
    }
  }

  /* ── Sort button ──────────────────────────────────────────────────── */

  function SortBtn({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <button
        onClick={() => toggleSort(col)}
        className={`flex items-center gap-1 text-left font-medium transition ${
          active ? "text-orange-400" : "text-gray-400 hover:text-gray-200"
        }`}
      >
        {label}
        <span className="text-xs">{active ? (sortAsc ? "↑" : "↓") : ""}</span>
      </button>
    );
  }

  const inputStyle = { background: "#111827", color: "#f9fafb" };
  const inputCls   = "rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none";

  const hasActiveFilters = !!(debouncedSearch || categoryFilter || statusFilter !== "all" || placementFilter || geomSrcFilter);

  /* ── Render ─────────────────────────────────────────────────────── */

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Products</h1>
          <p className="mt-0.5 text-sm text-gray-400">All products in the Fyrris catalogue</p>
        </div>
        <div className="flex items-center gap-2">
          {products.length > 0 && (
            <button
              onClick={exportCsv}
              className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition"
              title={selected.size > 0 ? `Export ${selected.size} selected` : "Export all filtered"}
            >
              {selected.size > 0 ? `Export ${selected.size} selected` : "Export CSV"}
            </button>
          )}
          <Link
            to="/admin/products/new"
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition"
          >
            + New Product
          </Link>
        </div>
      </div>

      {/* ── Stats bar — reflects current filtered set ── */}
      <div className="flex flex-wrap gap-3">
        <StatChip label={hasActiveFilters ? "Showing" : "Total"} value={stats.total} />
        <StatChip label="AR Ready"      value={stats.arReady}        accent="text-green-400" />
        <StatChip label="In Pipeline"   value={stats.processing}     accent="text-blue-400" />
        <StatChip label="Needs Attention" value={stats.needsAttention} accent="text-amber-400"
                  dim={stats.needsAttention === 0} />
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          <input
            type="text"
            placeholder="Search name, manufacturer, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle}
            className={inputCls + " w-full sm:w-72"}
          />
          {debouncedSearch && (
            <p className="text-xs text-gray-600 pl-1">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "{debouncedSearch}"
            </p>
          )}
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={inputStyle}
          className={inputCls}
        >
          <option value="">All categories</option>
          {CATEGORY_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
          ))}
        </select>

        <select
          value={placementFilter}
          onChange={(e) => setPlacementFilter(e.target.value)}
          style={inputStyle}
          className={inputCls}
          aria-label="Filter by placement type"
        >
          <option value="">All placements</option>
          {Object.entries(PLACEMENT_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={geomSrcFilter}
          onChange={(e) => setGeomSrcFilter(e.target.value)}
          style={inputStyle}
          className={inputCls}
          aria-label="Filter by geometry source"
        >
          <option value="">All sources</option>
          {Object.entries(GEOMETRY_SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={inputStyle}
          className={inputCls}
        >
          <option value="all">All pipeline statuses</option>
          <option value="complete">AR Ready</option>
          <option value="processing">Processing</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="needs_attention">Needs Attention</option>
          <option value="none">No AR</option>
        </select>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setSearch(""); setCategoryFilter(""); setStatusFilter("all");
              setPlacementFilter(""); setGeomSrcFilter("");
            }}
            className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2 transition"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Bulk action bar ── */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-gray-700 bg-gray-900/60 px-4 py-3">
          <span className="text-sm text-gray-300">
            <span className="font-medium text-white">{selected.size}</span>{" "}
            product{selected.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={bulkRerunPipeline}
              disabled={rerunning}
              className="rounded-lg border border-orange-700 bg-orange-950/40 px-3 py-1.5 text-sm text-orange-400 hover:bg-orange-900/40 disabled:opacity-50 transition"
            >
              {rerunning ? "Triggering…" : "Re-run Pipeline"}
            </button>
            <button
              onClick={() => setBulkConfirm(true)}
              className="rounded-lg border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:bg-red-900/20 transition"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">
          Loading products…
        </div>
      ) : filtered.length === 0 && products.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center space-y-4">
          <p className="text-gray-500">No products yet.</p>
          <Link
            to="/admin/products/new"
            className="inline-block rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-700 transition"
          >
            + Add your first product
          </Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-12 text-center text-sm text-gray-500">
          No products match your filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={paginated.length > 0 && paginated.every((p) => selected.has(p.id))}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-700 bg-gray-900 accent-orange-500"
                  />
                </th>
                <th className="px-3 py-3 text-left w-12"></th>
                <th className="px-4 py-3 text-left"><SortBtn col="name" label="Name" /></th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Category</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Manufacturer</th>
                <th className="px-4 py-3 text-left"><SortBtn col="base_price" label="Price" /></th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Pipeline</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Specs</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Status</th>
                <th className="px-4 py-3 text-left"><SortBtn col="created_at" label="Added" /></th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                  className="group border-t border-gray-800 hover:bg-gray-900/40 transition cursor-pointer"
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="rounded border-gray-700 bg-gray-900 accent-orange-500"
                    />
                  </td>

                  <td className="px-3 py-3">
                    <ProductThumb url={p.image_url} name={p.name} />
                  </td>

                  {/* Name + optional description */}
                  <td className="px-4 py-3 max-w-[220px]">
                    <span className="block font-medium truncate">{p.name}</span>
                    {p.description && (
                      <span className="block text-xs text-gray-500 truncate mt-0.5">{p.description}</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <CategoryBadge category={p.category} />
                  </td>
                  <td className="px-4 py-3">
                    <TypeBadges placementType={p.placement_type} geometrySource={p.geometry_source} />
                  </td>

                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {p.manufacturer_name}
                  </td>

                  <td className="px-4 py-3 text-gray-300 text-xs">
                    {p.base_price != null ? `$${p.base_price.toLocaleString()}` : "—"}
                  </td>

                  <td className="px-4 py-3">
                    <PipelineBadge product={p} />
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <SpecBadge  hasSpecs={!!p.consumer_specs_json} />
                      <ImageBadge hasImage={!!p.image_url} />
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${p.active ? "text-green-400" : "text-gray-500"}`}>
                      {p.active ? "Active" : "Draft"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap"
                      title={new Date(p.created_at).toLocaleString()}>
                    {relativeDate(p.created_at)}
                  </td>

                  {/* Actions — visible on hover; Re-run always visible when needed */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {needsRetrigger(p) && (
                        <button
                          onClick={() => rerunSingle(p.id)}
                          className="rounded border border-orange-700 bg-orange-950/40 px-2 py-0.5 text-xs text-orange-400 hover:bg-orange-900/40 transition whitespace-nowrap"
                        >
                          Re-run
                        </button>
                      )}
                      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          to={`/admin/products/${p.id}`}
                          className="rounded border border-gray-700 px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200 hover:border-gray-500 transition"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => setDeleteTarget({ id: p.id, name: p.name })}
                          className="rounded border border-red-900/50 px-2.5 py-1 text-xs text-red-500 hover:bg-red-900/20 hover:border-red-700 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer: count + pagination */}
          <div className="flex items-center justify-between border-t border-gray-800 px-4 py-2.5 text-xs text-gray-600">
            <span>
              {hasActiveFilters
                ? `${filtered.length} of ${products.length} products`
                : `${products.length} product${products.length !== 1 ? "s" : ""}`}
              {filtered.length > PAGE_SIZE && ` · page ${page + 1} of ${totalPages}`}
            </span>
            {filtered.length > PAGE_SIZE && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded border border-gray-700 px-3 py-1 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded border border-gray-700 px-3 py-1 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete dialogs ── */}
      {deleteTarget && (
        <DeleteConfirmDialog
          title={`Delete "${deleteTarget.name}"?`}
          description="This will permanently delete the product and all related assets. This cannot be undone."
          loading={deleteLoading}
          onConfirm={() => deleteProducts([deleteTarget.id])}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {bulkConfirm && (
        <DeleteConfirmDialog
          title={`Delete ${selected.size} product${selected.size !== 1 ? "s" : ""}?`}
          description="This will permanently delete all selected products and their related assets. This cannot be undone."
          confirmLabel={`Delete ${selected.size}`}
          loading={deleteLoading}
          onConfirm={() => deleteProducts(Array.from(selected))}
          onCancel={() => setBulkConfirm(false)}
        />
      )}
    </div>
  );
}
